import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import { buildQueryString } from "@/services/baseClient";
import type { Event, CreateEventRequest } from "@/types";

export function createEventsApi({ client }: HttpClient) {
  return {
    async getEvents(params?: {
      search?: string;
      userId?: string;
      trackId?: string;
      vehicleId?: string;
      upcoming?: boolean;
      past?: boolean;
      startDateFrom?: string;
      startDateTo?: string;
      limit?: number;
      offset?: number;
      orderBy?: "startDate" | "createdAt" | "name";
      orderDirection?: "asc" | "desc";
    }): Promise<{ events: Event[]; total?: number }> {
      const queryString = buildQueryString({
        search: params?.search,
        userId: params?.userId,
        trackId: params?.trackId,
        vehicleId: params?.vehicleId,
        upcoming: params?.upcoming,
        past: params?.past,
        startDateFrom: params?.startDateFrom,
        startDateTo: params?.startDateTo,
        limit: params?.limit,
        offset: params?.offset,
        orderBy: params?.orderBy,
        orderDirection: params?.orderDirection,
      });

      const response: AxiosResponse<{
        message?: string;
        events: Event[];
        total?: number;
      }> = await client.get(`/events${queryString}`);
      return { events: response.data.events, total: response.data.total };
    },

    async getEvent(id: string): Promise<Event> {
      const response: AxiosResponse<{ event: Event }> = await client.get(
        `/events/${id}`,
      );
      return response.data.event;
    },

    async createEvent(eventData: CreateEventRequest): Promise<Event> {
      const response: AxiosResponse<{ event: Event }> = await client.post(
        "/events",
        eventData,
      );
      return response.data.event;
    },

    async updateEvent(
      id: string,
      eventData: Partial<CreateEventRequest>,
    ): Promise<Event> {
      const response: AxiosResponse<{ event: Event }> = await client.put(
        `/events/${id}`,
        eventData,
      );
      return response.data.event;
    },

    async deleteEvent(id: string): Promise<void> {
      await client.delete(`/events/${id}`);
    },

    async uploadEventImage(
      eventId: string,
      file: File,
    ): Promise<{
      imageKey: string;
      imageSize: number;
    }> {
      const presignResponse = await client.get<{
        uploadUrl: string;
        tempKey: string;
        expiresIn: number;
      }>(`/events/${eventId}/image/presign`, {
        params: { contentType: file.type },
      });

      const { uploadUrl, tempKey } = presignResponse.data;

      const s3Response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!s3Response.ok) {
        throw new Error(
          `Failed to upload image to S3: ${s3Response.status} ${s3Response.statusText}`,
        );
      }

      const confirmResponse = await client.post<{
        imageKey: string;
        imageSize: number;
      }>(`/events/${eventId}/image/confirm`, {
        tempKey,
        contentType: file.type,
      });

      return confirmResponse.data;
    },

    async deleteEventImage(eventId: string): Promise<void> {
      await client.delete(`/events/${eventId}/image`);
    },

    async getUpcomingEvents(
      limit?: number,
      offset?: number,
    ): Promise<{ events: Event[]; total: number }> {
      const queryString = buildQueryString({
        limit: limit,
        offset: offset,
      });

      const response: AxiosResponse<{ events: Event[]; total: number }> =
        await client.get(`/events/upcoming${queryString}`);
      return response.data;
    },

    async searchEvents(
      query: string,
      limit?: number,
    ): Promise<{ events: Event[] }> {
      const queryString = buildQueryString({
        q: query,
        limit: limit || undefined,
      });

      const response: AxiosResponse<{ events: Event[] }> = await client.get(
        `/events/search${queryString}`,
      );
      return response.data;
    },

    async getEventsAttendedCount(): Promise<number> {
      const response: AxiosResponse<{
        success: boolean;
        data: { eventsAttended: number };
      }> = await client.get("/events/stats/attended");
      return response.data.data.eventsAttended;
    },
  };
}
